import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Chip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { green, red, amber } from "@mui/material/colors";

const RoomStatusDialog = ({
  open,
  room,
  action, // "activate" or "deactivate"
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [reason, setReason] = useState("");

  // Reset reason when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setReason("");
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm({
      is_active: action === "activate",
      reason: reason.trim() || undefined,
    });
    // Don't reset reason here - let the parent component handle dialog closing
  };

  const handleCancel = () => {
    setReason("");
    onCancel();
  };

  if (!room) return null;

  const isActivating = action === "activate";
  const isExpired = room.is_expired;

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleCancel : undefined} // Prevent closing during loading
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading} // Prevent escape key closing during loading
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isActivating ? (
            <CheckCircleIcon sx={{ color: green[500], fontSize: 28 }} />
          ) : (
            <BlockIcon sx={{ color: red[500], fontSize: 28 }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isActivating ? "Activate Room" : "Deactivate Room"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {room.name || `Room ${room.id?.slice(-8)}`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Room Status Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Current Status
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={room.is_active ? "Active" : "Inactive"}
              color={room.is_active ? "success" : "default"}
              size="small"
            />
            {isExpired && (
              <Chip
                label="Expired"
                color="error"
                size="small"
                icon={<WarningIcon fontSize="small" />}
              />
            )}
            <Chip
              label={room.type === "group" ? "Group Chat" : "Direct Message"}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>

        {/* Warning for expired rooms */}
        {isExpired && isActivating && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Cannot activate expired room.</strong> This room expired
              on{" "}
              {room.expiration_time
                ? new Date(room.expiration_time).toLocaleDateString()
                : "unknown date"}
              . Expired rooms cannot be reactivated.
            </Typography>
          </Alert>
        )}

        {/* Action confirmation */}
        {!isExpired && (
          <>
            <Alert severity={isActivating ? "info" : "warning"} sx={{ mb: 3 }}>
              <Typography variant="body2">
                {isActivating ? (
                  <>
                    <strong>Activate this room?</strong> Users will be able to
                    access and participate in this room again.
                  </>
                ) : (
                  <>
                    <strong>Deactivate this room?</strong> Users will no longer
                    be able to access this room. Existing participants will be
                    notified.
                  </>
                )}
              </Typography>
            </Alert>

            {/* Reason input */}
            <TextField
              fullWidth
              label={`Reason for ${action} (optional)`}
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                isActivating
                  ? "e.g., Terms violation resolved, Content approved..."
                  : "e.g., Terms violation, Inappropriate content, User reports..."
              }
              helperText={
                isActivating
                  ? "Explain why this room is being reactivated"
                  : "Explain why this room is being deactivated"
              }
              disabled={loading}
              sx={{ mt: 2 }}
            />
          </>
        )}

        {/* Room info */}
        <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Room ID: {room.id}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Participants: {room.participant_count || 0}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Created:{" "}
            {room.created_at
              ? new Date(room.created_at).toLocaleDateString()
              : "Unknown"}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleCancel} variant="outlined" disabled={loading}>
          Cancel
        </Button>

        {!isExpired && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={loading}
            color={isActivating ? "primary" : "error"}
            sx={{
              background: isActivating
                ? "linear-gradient(45deg, #4caf50, #66bb6a)"
                : "linear-gradient(45deg, #f44336, #ef5350)",
              "&:hover": {
                background: isActivating
                  ? "linear-gradient(45deg, #388e3c, #4caf50)"
                  : "linear-gradient(45deg, #d32f2f, #f44336)",
              },
            }}
          >
            {loading
              ? isActivating
                ? "Activating..."
                : "Deactivating..."
              : isActivating
              ? "Activate Room"
              : "Deactivate Room"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RoomStatusDialog;
